import { bunja } from 'bunja';
import { Atom, atom } from 'jotai';

import { calcFromEntryPercentage as calcFromEntryPercentageCore } from '@flipster/features/src/shared/calculator/tp-sl-preview';
import { typedRhfScope } from '@flipster/features/src/shared/react-hook-form';
import { ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import segment from '@flipster/utils/segment';
import { TradeEvent, TradeEventDataType } from '@flipster/utils/segment/events/trade';
import { V2OrderSideType } from '@flipster/utils/types/base';

import { TpslSchema } from './bunja/validation';

/** @deprecated Time-trigger sunset — legacy `TimeTriggerTpslModal` only. */
export interface TpslInfo {
  side: V2OrderSideType;
  positionType: 'open' | 'pending';
  tpSlType: 'tp' | 'sl';
  symbol: string;
  slot: number;
}

/** @deprecated Time-trigger sunset — legacy `TimeTriggerTpslModal` only. */
export const TpslFormScope = typedRhfScope<TpslSchema>();

/**
 * @deprecated Time-trigger orders have been sunset. State for legacy `TimeTriggerTpslModal` only.
 * New TP/SL work should use `features/src/tpsl-modal`.
 */
export const tpslModalBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);

  const tpslInfoAtom = atom<TpslInfo | null>(null);
  const isShowAtom = atom((get) => Boolean(get(tpslInfoAtom)));
  function openTpslModal(tpslInfo: TpslInfo) {
    store.set(tpslInfoAtom, tpslInfo);
  }
  function closeTpslModal() {
    store.set(tpslInfoAtom, null);
  }

  const currentOpenPositionAtom = createCurrentPositionAtom(tpslInfoAtom, openPositionsAtom);
  const currentPendingPositionAtom = createCurrentPositionAtom(tpslInfoAtom, pendingPositionsAtom);
  const currentPositionAtom = atom((get) => {
    const tpslInfo = get(tpslInfoAtom);
    const currentOpenPosition = get(currentOpenPositionAtom);
    const currentPendingPosition = get(currentPendingPositionAtom);
    if (!tpslInfo) return;
    if (tpslInfo.positionType === 'open') return currentOpenPosition;
    return currentPendingPosition;
  });

  return {
    isShowAtom,
    openTpslModal,
    closeTpslModal,
    tpslInfoAtom,
    currentOpenPositionAtom,
    currentPendingPositionAtom,
    currentPositionAtom,
  };
});
tpslModalBunja.debugLabel = 'tpslModalBunja';

export const tpslModalFormBunja = bunja(() => {
  const formContext = bunja.use(TpslFormScope);
  function getTriggerPrice() {
    return formContext.getValues('triggerPrice') || '0';
  }
  function getEntryPrice() {
    return formContext.getValues('entryPrice') || '0';
  }
  function calcFromEntryPercentage(triggerPrice = getTriggerPrice(), entryPrice = getEntryPrice()) {
    return calcFromEntryPercentageCore(triggerPrice, entryPrice);
  }
  return {
    formContext,
    getTriggerPrice,
    getEntryPrice,
    calcFromEntryPercentage,
  };
});
tpslModalFormBunja.debugLabel = 'tpslModalFormBunja';

export const tpslModalSegmentBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const tpslModalBunjaInstance = bunja.use(tpslModalBunja);
  const tpslModalFormBunjaInstance = bunja.use(tpslModalFormBunja);
  const { tpslInfoAtom, currentPositionAtom, currentPendingPositionAtom } = tpslModalBunjaInstance;
  const { getTriggerPrice, calcFromEntryPercentage } = tpslModalFormBunjaInstance;

  function trackTpslPriceClearClick() {
    const trackParams = getTrackParams();
    if (!trackParams) return;
    segment.track(TradeEvent.TpSlPriceClearClick, trackParams);
  }

  function trackTpslPriceEditClick() {
    const trackParams = getTrackParams();
    if (!trackParams) return;
    segment.track(TradeEvent.TpSlPriceEditClick, trackParams);
  }

  function trackTpslPriceResetClick() {
    const trackParams = getTrackParams();
    if (!trackParams) return;
    segment.track(TradeEvent.TpSlPriceResetClick, trackParams);
  }

  function trackTpslSettingConfirmClick() {
    const trackParams = getTrackParams();
    if (!trackParams) return;
    segment.track(TradeEvent.TpSlSettingConfirmClick, trackParams);
  }

  type TrackParams = TradeEventDataType[typeof TradeEvent.TpSlPriceClearClick];
  type FormRelatedParamNames = 'trigger_price' | 'from_entry_percentage';

  function getTrackParams(): TrackParams | undefined {
    const trackParams = store.get(trackParamsAtom);
    if (!trackParams) return;
    return { ...trackParams, ...getFormRelatedParams() };
  }

  function getFormRelatedParams(): Pick<TrackParams, FormRelatedParamNames> {
    const trigger_price = Number(getTriggerPrice());
    const from_entry_percentage = Number(calcFromEntryPercentage());
    return {
      trigger_price,
      from_entry_percentage,
    };
  }

  const trackParamsAtom = atom<Omit<TrackParams, FormRelatedParamNames> | undefined>((get) => {
    const tpslInfo = get(tpslInfoAtom);
    const currentPosition = get(currentPositionAtom);
    const currentPendingPosition = get(currentPendingPositionAtom);
    if (!tpslInfo || !currentPosition) return;
    const isMarketOrder = tpslInfo.positionType === 'open';
    const { symbol } = tpslInfo;
    const side = tpslInfo.side === 'Long' ? 'buy' : 'sell';
    const leverage = currentPosition.initLeverage;
    const tpsl_type = tpslInfo.tpSlType === 'tp' ? 'Take Profit' : 'Stop Loss';
    if (isMarketOrder) {
      const order_type = 'market_order';
      return { symbol, side, leverage, tpsl_type, order_type } as TrackParams;
    } else {
      if (!currentPendingPosition) return;
      const isLimitOrder = currentPendingPosition.orderType === 'Limit';
      const isTriggerPriceOrder = Boolean(currentPendingPosition.triggerTimestamp);
      const order_type = isLimitOrder
        ? 'limit_order'
        : isTriggerPriceOrder
          ? 'trigger_price_order'
          : 'trigger_time_order';
      return { symbol, side, leverage, tpsl_type, order_type } as TrackParams;
    }
  });

  return {
    trackTpslPriceClearClick,
    trackTpslPriceEditClick,
    trackTpslPriceResetClick,
    trackTpslSettingConfirmClick,
  };
});
tpslModalSegmentBunja.debugLabel = 'tpslModalSegmentBunja';

function createCurrentPositionAtom<T extends { slot: number }>(
  tpslInfoAtom: Atom<TpslInfo | null>,
  positionsAtom: Atom<Record<string, T[]> | null>,
): Atom<T | undefined> {
  return atom((get) => {
    const tpslInfo = get(tpslInfoAtom);
    const positions = get(positionsAtom);
    if (!tpslInfo || !positions) return;
    const { symbol, slot } = tpslInfo;
    const filteredPositions = positions[symbol];
    if (!filteredPositions) return;
    return filteredPositions.find((position) => position.slot === slot);
  });
}
